import {
  isDispositionId,
  isEmpty,
  isWorkflowActionId,
  type ProgramTaskCriteria,
  type ProgramTaskData,
  type UnsavedProgramTask,
} from '@terros/common'
import { isFormTaskType, type FormProgramTask, type FormTaskType } from './types'
import { PLACEHOLDER_ACTION_ID, PLACEHOLDER_STAGE_ID } from './taskType'
import { toFormExpiration } from './expiration'
import { minutesToTime, toFormDuration } from './duration'

export const toTaskFormValues = (
  task: UnsavedProgramTask | ProgramTaskData,
  tasks: ProgramTaskData[]
): FormProgramTask => {
  const type = getFormTaskType(task)
  const nextTaskIds = task.nextTaskIds ?? (task.nextTaskId ? [task.nextTaskId] : [])

  return {
    ...task,
    type,
    openSince: minutesToTime(task.openSince ?? 0),
    duration: toFormDuration(task),
    expiration: toFormExpiration(task),
    nextTaskIds,
    criteria: task.criteria ?? getDefaultCriteria(type),
    priority: task.priority ?? getPriority(tasks),
  }
}

const getPriority = (tasks: ProgramTaskData[]): number => {
  if (isEmpty(tasks)) return 1
  return Math.max(...tasks.map((t) => t.priority ?? 0)) + 1
}

const getFormTaskType = (task: Pick<UnsavedProgramTask, 'type' | 'criteria'>): FormTaskType => {
  switch (task.type) {
    case 'SalesActivity':
      return getSalesActivityType(task)
    case 'SalesTraining':
      return 'Shadowing'
    case 'RecordEvaluation':
      return getRecordingType(task)
    case 'WorkflowTasks':
      return getWorkflowType(task)
    default:
      if (isFormTaskType(task.type)) return task.type
      return 'WatchVideo'
  }
}

const getRecordingType = (task: Pick<UnsavedProgramTask, 'criteria'>): FormTaskType => {
  const criteria = task.criteria?.at(0)
  if (isEmpty(criteria)) return 'RecordConversation'
  if (criteria.type === 'convo') return 'RecordConversation'
  return 'RecordEvaluation'
}

const getSalesActivityType = (task: Pick<UnsavedProgramTask, 'criteria'>): FormTaskType => {
  const criteria = task.criteria?.at(0)
  if (isEmpty(criteria)) return 'PitchProspect'
  if (criteria.type !== 'activity') return 'PitchProspect'

  const activity = criteria.activityId.replace('Activity.', '')
  if (isDispositionId(activity) || activity === 'contact') return 'SalesPlanning'
  if (activity === 'pitch') return 'PitchProspect'
  return 'SalesOutput'
}

const getWorkflowType = (task: Pick<UnsavedProgramTask, 'criteria'>): FormTaskType => {
  const criteria = task.criteria?.at(0)
  if (isEmpty(criteria) || criteria.type !== 'workflow') return 'WorkflowAction'
  if (isWorkflowActionId(criteria.stageOrActionId)) return 'WorkflowAction'
  return 'WorkflowStage'
}

const getDefaultCriteria = (type: FormTaskType): ProgramTaskCriteria[] => {
  switch (type) {
    case 'WatchVideo':
      return [{ type: 'video' }]
    case 'SendMessage':
      return [{ type: 'videoChat' }]
    case 'SalesPlanning':
      return [{ type: 'activity', activityId: 'contact', count: 1 }]
    case 'PitchProspect':
      return [{ type: 'activity', activityId: 'pitch', count: 1 }]
    case 'Shadowing':
      return [{ type: 'shadow', target: 'manager', completeWhen: 'scheduled' }]
    case 'RecordConversation':
      return [{ type: 'convo' }]
    case 'WorkflowAction':
      return [{ type: 'workflow', stageOrActionId: PLACEHOLDER_ACTION_ID, count: 1 }]
    case 'WorkflowStage':
      return [{ type: 'workflow', stageOrActionId: PLACEHOLDER_STAGE_ID, count: 1 }]
    default:
      return []
  }
}
